import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import AuctionVideoStream from '@/pages/BiddingConsole/components/AuctionVideoStream/AuctionVideoStream';
import MessageScroller from '@/pages/BiddingConsole/components/MessageScroller/MessageScroller';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getLocale } from '@/redux/modules/intl';
import { getItemSummaryLotNumber, getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { hasVideoStream } from '@/redux/modules/video';
import { popupCenter } from '@/utils/popupCenter';
import { getAuctionVideoUrl } from '@/utils/urls';

import AuctionLotsScroller from './components/AuctionLotsScroller/AuctionLotsScroller';
import AuctionLotTracker from './components/AuctionLotTracker/AuctionLotTracker';
import AuctionNameAndSettingsBlock from './components/AuctionNameAndSettingsBlock/AuctionNameAndSettingsBlock';
import CurrentItemDetails from './components/CurrentItemDetails/CurrentItemDetails';
import DesktopBidContainer from './components/DesktopBiddingContainer/DesktopBidContainer';

type Props = {
    catalogId: number;
};

const WIDTH = 1024;
const HEIGHT = 576;

const DesktopBiddingConsole = ({ catalogId }: Props) => {
    const windowRef = React.useRef(null);
    let timerRef = React.useRef<ReturnType<typeof setInterval>>(null);
    const [videoPoppedOut, setVideoPoppedOut] = React.useState(false);
    const locale = useAppSelector(getLocale);
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const liveItemTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const liveItemLotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));

    const localePath = locale === 'en-gb' ? `/${locale}` : '';
    const [selectedCurrency, setSelectedCurrency] = React.useState<string>();
    const catalogHasVideoStream = useAppSelector((state) => hasVideoStream(state, catalogId));

    const openVideoConsole = React.useCallback(() => {
        windowRef.current = popupCenter({
            height: HEIGHT,
            title: `${title} Video`,
            url: localePath + getAuctionVideoUrl(catalogId),
            width: WIDTH,
        });
        setVideoPoppedOut(true);

        timerRef.current = setInterval(function () {
            // Apparently this is the standard security work around for keeping track of whether a newly opened window has closed
            if (windowRef.current.closed) {
                clearInterval(timerRef.current);
                setVideoPoppedOut(false);
            }
        }, 1000);
    }, [catalogId, localePath, title]);
    return (
        <PageContainer>
            <GreyBackgroundArea />
            <ConsoleContainer>
                <LeftPaneSection>
                    <AuctionNameAndSettingsBlock
                        catalogId={catalogId}
                        onCurrencyChanged={setSelectedCurrency}
                        selectedCurrency={selectedCurrency}
                    />
                    <AuctionLotTracker catalogId={catalogId} />
                    <AuctionLotsScroller catalogId={catalogId} />
                </LeftPaneSection>
                <RightPaneSection>
                    <RightPaneGrid>
                        <PlacedHeader>
                            <H2>
                                <FormattedMessage
                                    id="number_and_title"
                                    values={{
                                        lotNumber: liveItemLotNumber,
                                        title: liveItemTitle,
                                    }}
                                />
                            </H2>
                        </PlacedHeader>
                        <PlacedCurrentItemDetails catalogId={catalogId} />
                        <PlacedCTA
                            itemId={itemId}
                            selectedCurrency={selectedCurrency}
                        />
                        <RightStatusSection>
                            {catalogHasVideoStream && !videoPoppedOut && (
                                <AuctionVideoStream
                                    catalogId={catalogId}
                                    className="z-[1] flex-none"
                                    onOpenInNewWindow={openVideoConsole}
                                />
                            )}
                            <MessageScroller
                                catalogId={catalogId}
                                convert={false}
                                hideMessages={false}
                            />
                        </RightStatusSection>
                    </RightPaneGrid>
                </RightPaneSection>
            </ConsoleContainer>
            <WhiteBackgroundArea />
        </PageContainer>
    );
};

export default DesktopBiddingConsole;

const PageContainer = styled.div`
    max-width: 1500px;
    margin: 0 auto;
`;

const GreyBackgroundArea = styled.div`
    background: ${({ theme }) => theme.colors.grey500};
`;

const WhiteBackgroundArea = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
`;

const ConsoleContainer = styled.div`
    height: 100vh;
    display: grid;
    grid-template-rows: auto auto min-content;
    grid-template-areas:
        'lot-tracker right-pane-section'
        'lot-tracker right-pane-section'
        'lot-tracker right-pane-section';
    grid-template-columns: 372px 1fr;
`;

const LeftPaneSection = styled.section`
    grid-area: lot-tracker;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.grey500};
`;

const RightPaneSection = styled.section`
    grid-area: right-pane-section;
`;

const RightPaneGrid = styled.div`
    height: 100vh;
    display: grid;
    grid-template-rows: min-content auto min-content;
    grid-template-columns: 1fr 444px;
    grid-template-areas:
        'lot-header lot-header'
        'current-item message-scroller'
        'footer-CTA footer-CTA';

    @media (max-width: 1400px) {
        grid-template-columns: 1fr 340px;
    }

    @media (max-width: 1100px) {
        grid-template-columns: 1fr 220px;
    }
`;

const PlacedCurrentItemDetails = styled(CurrentItemDetails)`
    grid-area: current-item;
    padding-right: 24px;
`;

const RightStatusSection = styled.section`
    grid-area: message-scroller;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.2s ease-in;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    background: ${({ theme }) => theme.colors.white};
    margin-right: 24px;
    margin-bottom: 16px;
    overflow: hidden;

    @media (min-height: 1500px) {
        border-left: 1px solid ${({ theme }) => theme.colors.grey400};
    }
`;

const PlacedCTA = styled(DesktopBidContainer)`
    grid-area: footer-CTA;
    transition: all 0.2s ease-in;
`;

const PlacedHeader = styled.section`
    grid-area: lot-header;
    padding: 32px 0 16px 0;
    margin: 0 24px;
    transition: all 0.2s ease-in;
    background: ${({ theme }) => theme.colors.white};
    text-transform: capitalize;
`;
